"use client";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import { PRODUCT_VARIATION_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetProductVariations from "@/hooks/requests/product-variations/use-get-product-variations";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import IProductVariation from "@/interfaces/variation.interface";
import { VariationsMessages } from "@/messages/variation/variations.messages";

import VariationFiltersForm from "./components/VariationFiltersForm";
import { ProductVariationTable } from "./components/VariationTable";
import { initialVariationFilterValues } from "./constants";

type Props = {
  messages: VariationsMessages;
};

function VariationsPage({ messages }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;
  const hasNoPermissionToCreate = useHasNoPermission(PRODUCT_VARIATION_PERMISSIONS.CREATE);
  const filter = useFilters<IProductVariation>({ initialValue: initialVariationFilterValues });
  const filters = filter.filters;
  const { data, isLoading, error } = useGetProductVariations({ filters, pageNumber, pageSize });

  const hasProductVariations = data && data.items.length > 0;
  const noProductVariations = data && data.items.length === 0;

  const shouldLoadProductVariationsTable = hasProductVariations && !isLoading && !error;
  const shouldLoadError = !isLoading && !!error;
  const isString = typeof data === "string";
  const shouldLoadNoFound = (noProductVariations || isString) && !isLoading && !error;

  return (
    <PageLayout messages={messages} title={messages.pageTitle}>
      <PageContainer>
        <Formik initialValues={filters} validateOnChange={false} onSubmit={filter.changeFilters}>
          <VariationFiltersForm
            messages={messages}
            temporaryFilters={filter.temporaryFilters}
            addNewTemporaryFilter={filter.addNewTemporaryFilter}
            deleteTemporaryFilter={filter.deleteTemporaryFilter}
          />
        </Formik>

        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(16) })}>
          <Suspense isSuspended={isLoading} fallback={<CircularProgress size={50} />}>
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6">{messages.pageTitle}</MDTypography>

                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={() => router(ROUTE_URLS.createProductVariation(lang))}
                  disabled={hasNoPermissionToCreate}
                  isLoading={isLoading}
                  endIcon={<AddCircleOutlineIcon />}
                >
                  {messages.addVariationButton}
                </MDButton>
              </MDBox>

              {!!shouldLoadProductVariationsTable && (
                <ProductVariationTable pagination={pagination} data={data} messages={messages} />
              )}

              {!!shouldLoadError && (
                <AlertBlock
                  title={messages.errorToLoad}
                  description={error.message}
                  alt={messages.notFoundAlt}
                />
              )}

              {!!shouldLoadNoFound && (
                <AlertBlock
                  title={messages.notFoundError}
                  description={messages.notFoundDescription}
                  alt={messages.notFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}

export default VariationsPage;
